/**
 * EphemeralDataSetをDerivedDataSetに変換する機能
 */

import type { EphemeralDataSet, DerivedDataSet, FilterOperation, FilterExpression } from '../../types';
import { createDatasetId, createTransformOperationId } from '../../types';
import { markAsTransient } from '../../types/serialization.types';
import logger from './logger';

/**
 * 2つのFilterExpressionをANDで合成
 *
 * @param baseExpression 元のフィルタ式
 * @param additionalExpression 追加するフィルタ式
 * @returns 合成されたフィルタ式
 */
function mergeFilterExpressions(
  baseExpression: FilterExpression,
  additionalExpression: FilterExpression
): FilterExpression {
  // 既にANDのCompoundConditionの場合は、conditionsに追加
  if ('type' in baseExpression && baseExpression.type === 'compound' && baseExpression.operator === 'and') {
    return {
      ...baseExpression,
      conditions: [...baseExpression.conditions, additionalExpression]
    };
  }

  // それ以外は新しいCompoundConditionを作成
  return {
    type: 'compound',
    operator: 'and',
    conditions: [baseExpression, additionalExpression]
  };
}

/**
 * EphemeralDataSetをDerivedDataSetに変換
 *
 * filterExpression付きのEphemeralDataSetを、再計算可能なDerivedDataSetに変換します。
 * この変換により、MDSプロジェクトファイルに保存でき、元データ更新時に自動再計算されるようになります。
 *
 * @param ephemeralDataset 変換元のEphemeralDataSet
 * @param name 新しいDerived Datasetの名前
 * @returns 変換されたDerivedDataSet
 * @throws filterExpressionが設定されていない場合にエラー
 * @throws sourceDatasetIdが設定されていない場合にエラー
 */
export function convertEphemeralToDerivedDataset(
  ephemeralDataset: EphemeralDataSet,
  name: string
): DerivedDataSet {
  // メタデータの検証
  if (!ephemeralDataset.metadata?.filterExpression) {
    throw new Error('EphemeralDataSet does not have filterExpression metadata');
  }

  if (!ephemeralDataset.metadata?.sourceDatasetId) {
    throw new Error('EphemeralDataSet does not have sourceDatasetId metadata');
  }

  const { sourceDatasetId, filterExpression, description, additionalFilterExpression } = ephemeralDataset.metadata;

  logger.info('[convertEphemeralToDerivedDataset] Converting ephemeral to derived dataset', {
    sourceDatasetId,
    filterDescription: description,
    hasAdditionalFilter: !!additionalFilterExpression,
    rowCount: ephemeralDataset.rowCount
  });

  // 追加フィルタがある場合は元のフィルタとANDで合成
  const finalFilterExpression = additionalFilterExpression
    ? mergeFilterExpressions(filterExpression, additionalFilterExpression)
    : filterExpression;

  // FilterOperationを作成
  const operation: FilterOperation = {
    id: createTransformOperationId(`transform-${crypto.randomUUID()}`),
    type: 'filter',
    targetDatasetId: sourceDatasetId,
    filterExpression: finalFilterExpression
  };

  // DerivedDataSetを構築
  const derivedDataset: DerivedDataSet = {
    id: createDatasetId(`dataset-derived-${crypto.randomUUID()}`),
    name,
    type: 'derived',
    schema: ephemeralDataset.schema,
    rowCount: ephemeralDataset.rowCount,
    data: ephemeralDataset.data,  // 初期データをキャッシュとして保持
    parentDatasetIds: [sourceDatasetId],
    operation,
    metadata: {
      createdAt: new Date().toISOString(),
      description: description || 'Filtered dataset',
      basedOn: [sourceDatasetId]
    }
  };

  // dataプロパティをtransientとしてマーク（MDSファイル保存時に除外）
  markAsTransient(derivedDataset, ['data']);

  logger.info('[convertEphemeralToDerivedDataset] Conversion completed', {
    derivedDatasetId: derivedDataset.id,
    name: derivedDataset.name
  });

  return derivedDataset;
}
